import React, { useState, useEffect, useContext } from 'react';
import { StoreContext } from '../Hooks/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import makeStyles from '@mui/styles/makeStyles';
import Todos from './Todos';
import ArchivedTodolists from './ArchivedTodolists';
import TodoComments from './TodoComments';
import { getTodoLists, createTodoList } from '../../api/todo';
import useUserInteractionTracker from '../Hooks/useUserInteractionTracker'; // Import the hook

// Alert component for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const useStyles = makeStyles((theme) => ({
  iconwidth: {
    minWidth: '35px !important',
  },
  menuwidth: {
    width: 'inherit',
  },
  menuDiv: { marginLeft: '10px' },
  activitiesMenu: {
    fontSize: '12pt',
    verticalAlign: 'middle',
    marginBottom: '5px',
    display: 'inline-block',
  },
  tctitle: {
    fontFamily: 'Montserrat',
    fontSize: '11pt',
    backgroundColor: '#00416e',
    color: '#fff',
    textAlign: 'center',
  },
  b1: {
    backgroundColor: '#fff',
    minWidth: '320px',
    marginLeft: '-5px',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

export default function TodoList({ open, onClose }) {
  const context = React.useContext(StoreContext);
  const {
    modaldialog,
    todolistheader,
    todolistheaderid,
    todolistsaved,
    thisuserid,
    showarchiveflag,
    tododirtycheck,
    thisdeviceid,
    thislocationid,
    thissessionid,
    mytodolists,
    showComments,
    commentsListId,
  } = context;

  // Initialize the user interaction tracker
  const { trackInteraction } = useUserInteractionTracker();

  const [, setLoadedmodal] = modaldialog;
  const [dirtytodos, setdirtytodos] = tododirtycheck;
  const [todoHeader, setTodoHeader] = todolistheader;
  const [todolistid, settodolistid] = todolistheaderid;
  const [todos, setTodos] = todolistsaved;
  const [userid, setUserid] = thisuserid;
  const [showarchived, setShowarchived] = showarchiveflag;
  const [deviceid] = thisdeviceid;
  const [locationid] = thislocationid;
  const [sessionid, setSessionid] = thissessionid;
  const [todolists, settodolists] = mytodolists;
  const [showCommentsState, setShowComments] = showComments;
  const [commentsListIdState, setCommentsListId] = commentsListId;
  const [showTodosState, setShowTodos] = context.showTodos;
  
  // State for creating a new todo list when none exist
  const [isCreatingFirstList, setIsCreatingFirstList] = useState(false);
  const [newListName, setNewListName] = useState('');
  
  // Add isCreatingFirstList to context for access by Todos component
  context.isCreatingFirstList = [isCreatingFirstList, setIsCreatingFirstList];

  const classes = useStyles();

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  /**
   * Use React Query to fetch todo lists for the user
   */
  const queryClient = useQueryClient();

  const { 
    data: todoListsData, 
    isLoading: todoListsLoading, 
    error: todoListsError,
    refetch: refetchTodoLists,
    isRefetching: isRefetchingLists
  } = useQuery({
    queryKey: ['todoLists', userid],
    queryFn: async () => {
      try {
        // console.log('Fetching todo lists from server...');
        const lists = await getTodoLists(userid);
        // console.log('Fetched todo lists:', lists);
        return lists || [];
      } catch (error) {
        console.error('Error in todo lists queryFn:', error);
        throw error;
      }
    },
    enabled: !!userid && open,
    staleTime: 0, // Always consider data stale to force refetch
    cacheTime: 1000 * 60, // Cache for 1 minute only
    onSuccess: (data) => {
      // console.log('Todo lists query success, data:', data);
      if (data && Array.isArray(data)) {
        // Log the first few lists to help diagnose issues
        // console.log('First 3 lists:', data.slice(0, 3).map(list => ({
        //   id: list.id,
        //   name: list.list_name,
        //   archived: list.archived
        // })));
        
        // Force a deep clone to ensure React detects the change
        const freshData = JSON.parse(JSON.stringify(data));
        
        // Update both the context state and local query data
        // console.log('Updating todolists state with fresh data');
        settodolists(freshData);
        
        // Check if user has any todolists
        if (freshData.length === 0) {
          // If no todolists exist, show the create todolist UI
          setShowTodos(false);
          setShowarchived(true);
          setIsCreatingFirstList(true);
        } else {
          // If no list is selected, select the first one
          if (!todolistid && freshData.length > 0) {
            settodolistid(freshData[0].id);
            setTodoHeader(freshData[0].list_name);
          } else if (todolistid) {
            // Update the header with the current list name
            const currentList = freshData.find(list => list.id === todolistid);
            if (currentList) {
              // console.log('Updating header for list:', currentList.id, 'to:', currentList.list_name);
              setTodoHeader(currentList.list_name);
            } else {
              // console.log('Could not find list with id:', todolistid, 'in fresh data');
            }
          }
        }
      } else {
        console.warn('Received invalid data from server:', data);
        settodolists([]);
        // If no valid data, show the create todolist UI
        setShowTodos(false);
        setShowarchived(true);
        setIsCreatingFirstList(true);
      }
    },
    onError: (error) => {
      console.error('Error fetching todo lists:', error);
      showSnackbar('Error fetching todo lists', 'error');
    }
  });

  // Force refresh when dialog opens and handle cross-browser sync
  useEffect(() => {
    const refreshData = async () => {
      if (open && userid) {
        //console.log('Dialog opened, forcing refresh of todo lists');
        
        try {
          // Reset queries completely - this is stronger than removeQueries
          //console.log('Resetting todo lists query cache');
          await queryClient.resetQueries({
            queryKey: ['todoLists', userid],
            exact: true
          });
          
          // Force fresh fetch with no cache - ALWAYS prioritize database values
          //console.log('Forcing refetch of todo lists from database');
          const result = await refetchTodoLists({
            refetchOptions: { 
              force: true,
              throwOnError: true
            }
          });
          
          //console.log('Refetch completed successfully:', result);
          
          // Ensure the context state is updated with the fresh data from database
          if (result.data && Array.isArray(result.data)) {
            // console.log('Manually updating context with fresh database data');
            // Force a deep clone to ensure React detects the change
            const freshData = JSON.parse(JSON.stringify(result.data));
            
            // Update context state with database values
            settodolists(freshData);
            
            // If a list is currently selected, ensure its name is up-to-date
            if (todolistid) {
              const currentList = freshData.find(list => list.id === todolistid);
              if (currentList) {
                // console.log('Updating header with fresh database value:', currentList.list_name);
                setTodoHeader(currentList.list_name);
              }
            }
          }
        } catch (error) {
          console.error('Error refreshing todo lists:', error);
        }
      }
    };
    
    refreshData();
  }, [open, userid, queryClient, refetchTodoLists, settodolists, todolistid, setTodoHeader]);

  /**
   * Handle creating a new todo list.
   * @param {object} newList - The newly created todo list.
   */
  const handleCreateTodoList = async (newList) => {
    try {
      // Clear todos state when creating a new list
      setTodos([]);
      
      // Update local state by replacing any temp list with the real one
      settodolists(prev => [...prev.filter(list => list.id !== newList.id), newList]);
      
      // Select the new list
      settodolistid(newList.id);
      setTodoHeader(newList.list_name);
      
      // Ensure mutual exclusivity - set showTodos to true, others to false
      setShowTodos(true);
      setShowarchived(false);
      setShowComments(false);
      
      // Reset creating first list state
      setIsCreatingFirstList(false);
      
      // Invalidate and reset queries for the new list
      queryClient.invalidateQueries(['todoItems', newList.id, userid]);
      queryClient.resetQueries(['todoItems', newList.id, userid]);
      
      showSnackbar('New todo list created!', 'success');
    } catch (error) {
      console.error('Error handling new todo list:', error);
      // Rollback local state update
      settodolists(prev => prev.filter(list => list.id !== newList.id));
      showSnackbar('Error saving todo list', 'error');
    }
  };
  
  /**
   * Handle creating the first todo list directly from the dialog
   */
  const handleCreateFirstList = async () => {
    if (newListName.trim() === '') {
      showSnackbar('List name cannot be empty', 'error');
      return;
    }

    try {
      const newList = await createTodoList(userid, newListName);
      
      // Track the create action
      trackInteraction('todolist_create', {
        componentName: 'TodoList',
        timestamp: Date.now(),
        userId: userid
      });
      
      // Handle the new list creation
      await handleCreateTodoList(newList);
      
      // Reset state
      setNewListName('');
      setIsCreatingFirstList(false);
    } catch (error) {
      console.error('Error creating first todo list:', error);
      showSnackbar('Error creating todo list', 'error');
    }
  };

  /**
   * Handle selecting a todo list.
   * @param {string} listId - The ID of the selected todo list.
   * @param {string} listName - The name of the selected todo list.
   */
  const handleSelectTodoList = (listId, listName) => {
    // Clear todos before setting new list
    setTodos([]);
    
    // Set the new list id and header
    settodolistid(listId);
    setTodoHeader(listName);
    
    // Ensure mutual exclusivity - set showTodos to true, others to false
    setShowTodos(true);
    setShowarchived(false);
    setShowComments(false);
    
    // Force a state update and refresh todos
    setLoadedmodal(prev => !prev);
    queryClient.invalidateQueries(['todoItems', listId, userid]);
    queryClient.resetQueries(['todoItems', listId, userid]);
  };

  /**
   * Handle closing the dialog.
   * Ensures that the cache is invalidated when the dialog is closed
   * so that fresh data is fetched when it's reopened.
   */
  const handleClose = async () => {
    console.log('in handleClose')
    console.log('handleClose called, current states:', {
      showCommentsState,
      showarchived,
      isTransitioning: context.isTransitioning,
      disableTodoComments: context.disableTodoComments[0]
    });
    
    // If we're in comments view, exit it first
    if (showCommentsState) {
      console.log('In comments view, transitioning to archived lists view before closing');
      try {
        await context.safeExitComments();
      } catch (error) {
        console.error('Error during safeExitComments:', error);
      }
    }
    
    // Clear the cache when closing the dialog
    if (userid) {
      console.log('Dialog closing, resetting todo lists cache');
      try {
        await queryClient.resetQueries({
          queryKey: ['todoLists', userid],
          exact: true
        });
        
        if (todolistid) {
          await queryClient.resetQueries({
            queryKey: ['todoItems', todolistid],
            exact: true
          });
        }
      } catch (error) {
        console.error('Error resetting cache:', error);
      }
    }
    
    // Call the parent's onClose handler
    console.log('Calling parent onClose handler');
    onClose();
  };

  /**
   * Toggle between the three mutually exclusive states:
   * 1. Comments view (showCommentsState = true)
   * 2. Archived lists view (showarchived = true)
   * 3. Current list view (showTodosState = true)
   */
  const toggleShowArchiveFlag = () => {
    console.log('toggleShowArchiveFlag called, current states:', {
      showCommentsState,
      showarchived,
      showTodosState,
      todolistid,
      todoHeader,
      isCreatingFirstList,
      disableTodoComments: context.disableTodoComments[0]
    });
    
    // STATE 1: If we're in comments view, exit it and show the archived lists view
    if (showCommentsState) {
      console.log('In comments view, transitioning to archived lists view using safeExitComments');
      // Use the centralized function from store.js to handle the transition
      context.safeExitComments();
      return;
    }
    
    // If already transitioning or comments rendering is disabled, do nothing
    if (context.isTransitioning || context.disableTodoComments[0]) {
      console.log('Already transitioning or comments rendering is disabled, ignoring duplicate call');
      return;
    }
    
    // Don't allow toggling if we're in archived view and creating first list
    if (showarchived && isCreatingFirstList) {
      console.log('In archived view and creating first list, ignoring toggle request');
      return;
    }
    
    // Toggle between STATE 2 and STATE 3
    if (!showarchived) {
      // STATE 3 -> STATE 2: Switch from current list view to archived lists view
      console.log('Switching from current list to archived view');
      
      // Ensure mutual exclusivity - set showTodos to false and showarchived to true
      setShowTodos(false);
      setShowarchived(true);
      
      // Then update list ID and header
      setTimeout(() => {
        console.log('Archived view setTimeout callback executing');
        settodolistid(null);
        setTodoHeader('All Lists');
      }, 100);
    } else {
      // STATE 2 -> STATE 3: Switch from archived view to current list view
      console.log('Switching from archived view to current list view');
      
      // Ensure mutual exclusivity - set showTodos to true and showarchived to false
      setShowTodos(true);
      setShowarchived(false);
    }
  };

  // Track todo_list_dialog when dialog opens or view changes
  useEffect(() => {
    if (open && userid) {
      let viewType = 'active_list';
      
      if (showCommentsState) {
        viewType = 'comments_view';
      } else if (showarchived) {
        viewType = 'archived_lists';
      }
      
      trackInteraction('todo_list_dialog_view', { 
        componentName: 'TodoList',
        timestamp: Date.now(),
        userId: userid,
        viewType
      });
    }
  }, [open, userid, showarchived, showCommentsState]);

  return (
    <>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      <Dialog 
        open={open} 
        onClose={handleClose}
        fullWidth
        maxWidth="md"
      >
      <DialogTitle className={classes.tctitle} onClose={handleClose} disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            {/* Only show the hamburger menu in the Comments view */}
            {showCommentsState && (
              <IconButton 
                onClick={toggleShowArchiveFlag}
                color="inherit"
                size="small"
                title="Back to lists"
                sx={{ color: 'white' }}
              >
                <MenuOutlinedIcon sx={{ color: 'white' }} />
              </IconButton>
            )}
            <Typography variant="h6" component="div">
              {showCommentsState ? 'Comments & Summary' : (showarchived ? 'All Lists' : (todoHeader || 'Select a List'))}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="large"
            edge="end"
            sx={{ color: 'white' }}
          >
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.b1} style={{ paddingTop: '4px', width: '100%', maxWidth: '100%' }}>
        {todoListsLoading || isRefetchingLists || context.isTransitioning ? (
          <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
              {context.isTransitioning ? "Transitioning..." : "Loading..."}
            </Typography>
          </Box>
        ) : todoListsError ? (
          <Typography color="error" mt={4}>
            Error loading todo lists: {todoListsError.message}
          </Typography>
        ) : showCommentsState && !context.disableTodoComments[0] ? (
          <React.Fragment key={`comments-${commentsListIdState}`}>
            <TodoComments 
              todolistid={commentsListIdState} 
              onBack={() => {
                console.log('TodoComments onBack called - using safeExitComments from store');
                // Use the centralized function from store.js to handle the transition
                context.safeExitComments();
              }}
            />
          </React.Fragment>
        ) : isCreatingFirstList ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Create Your First Todo List
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              First, create a task list (like a bucket for your todos). Then, add your tasks to the list.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="New List Name"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              margin="normal"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newListName.trim() !== '') {
                  handleCreateFirstList();
                }
              }}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button 
                variant="outlined" 
                onClick={() => {
                  // Close the entire dialog when canceling the first todolist creation
                  handleClose();
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleCreateFirstList}
                disabled={newListName.trim() === ''}
              >
                Create List
              </Button>
            </Box>
          </Box>
        ) : (
          showarchived ? (
            <ArchivedTodolists
              todolists={todoListsData || []}
              onSelectTodoList={handleSelectTodoList}
              onCreateTodoList={handleCreateTodoList}
            />
          ) : (
            <Todos todolistid={todolistid} />
          )
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
